import React, { useState } from 'react';

export const ParticipantDetailsPopup = ({
  show,
  onClose,
  participant,
  details,
  loading,
  error,
  onRetry,
  formatCurrency,
  formatDate
}) => {
  const [showImageZoom, setShowImageZoom] = useState(false);

  if (!show) return null;

  const getGenderIcon = (gender) => {
    const genderMap = {
      'M': { color: 'blue', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
      'F': { color: 'pink', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' }
    };
    return genderMap[gender?.toUpperCase()] || { color: 'gray', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' };
  };

  const getGenderText = (gender) => {
    switch (gender?.toUpperCase()) {
      case 'M': return 'Male';
      case 'F': return 'Female';
      default: return 'Other';
    }
  };

  const genderStyle = details ? getGenderIcon(details.Gender) : { color: 'gray', icon: '' };

  // Image Zoom Modal Component
  const ImageZoomModal = () => {
    if (!showImageZoom) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4 animate-fadeIn"
        onClick={() => setShowImageZoom(false)}
      >
        <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={() => setShowImageZoom(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors p-2 rounded-full hover:bg-white/10 z-10"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Zoom controls */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black bg-opacity-50 rounded-lg p-2">
            <button
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                const img = document.getElementById('zoomed-image');
                if (img) {
                  const currentScale = img.style.transform ? parseFloat(img.style.transform.replace('scale(', '')) : 1;
                  img.style.transform = `scale(${currentScale + 0.25})`;
                }
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                const img = document.getElementById('zoomed-image');
                if (img) {
                  const currentScale = img.style.transform ? parseFloat(img.style.transform.replace('scale(', '')) : 1;
                  if (currentScale > 0.5) {
                    img.style.transform = `scale(${currentScale - 0.25})`;
                  }
                }
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <button
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                const img = document.getElementById('zoomed-image');
                if (img) {
                  img.style.transform = 'scale(1)';
                }
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* Zoomed image */}
          {details.PhotoPath_Url ? (
            <img
              id="zoomed-image"
              src={details.PhotoPath_Url}
              alt={details.Name}
              className="max-w-full max-h-full object-contain cursor-zoom-out transition-transform duration-200"
              onClick={(e) => e.stopPropagation()}
              style={{ transform: 'scale(1)' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(details.Name)}&background=${genderStyle.color}&color=fff&size=500`;
              }}
            />
          ) : (
            <div className="w-96 h-96 rounded-full bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-2xl">
              <span className="text-white text-8xl font-bold">
                {details.Name?.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Main Popup */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden transform transition-all animate-slideIn">
          {/* Header */}
          <div className={`px-6 py-4 border-b border-gray-200 bg-blue-700 rounded-t-2xl`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Participant Details</h3>
                  <p className="text-white text-sm opacity-90">
                    {participant?.name} • {participant?.email}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-white/80 transition-colors p-1 rounded-full hover:bg-white/10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
            {loading ? (
              <div className="py-12 text-center">
                <div className="relative inline-block">
                  <div className="w-12 h-12 border-4 border-blue-200 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-12 h-12 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
                </div>
                <p className="mt-4 text-gray-600">Loading participant details...</p>
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <div className="inline-block p-6 bg-red-50 rounded-2xl border border-red-200">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">Failed to load details</h3>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={onRetry}
                    className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-blue-600 transition-all"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : details ? (
              <div className="space-y-6">
                {/* Profile Section */}
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Avatar with zoom functionality */}
                  <div className="md:w-1/3">
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center border border-gray-200">
                      <div className="relative inline-block group">
                        {details.PhotoPath_Url ? (
                          <>
                            <img
                              src={details.PhotoPath_Url}
                              alt={details.Name}
                              className="w-24 h-24 rounded-full mx-auto border-4 border-white shadow-lg cursor-pointer transition-transform group-hover:scale-105"
                              onClick={() => setShowImageZoom(true)}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(details.Name)}&background=${genderStyle.color}&color=fff&size=200`;
                              }}
                            />
                            {/* Zoom overlay indicator */}
                            <div 
                              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => setShowImageZoom(true)}
                            >
                              <div className="bg-black bg-opacity-50 rounded-full w-24 h-24 flex items-center justify-center cursor-pointer">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div 
                            className={`w-24 h-24 rounded-full bg-gradient-to-br from-${genderStyle.color}-500 to-${genderStyle.color}-600 flex items-center justify-center mx-auto shadow-lg cursor-pointer group-hover:scale-105 transition-transform`}
                            onClick={() => setShowImageZoom(true)}
                          >
                            <span className="text-white text-3xl font-bold">
                              {details.Name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-gray-800 mt-4">
                        {details.Prefix} {details.Name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">{details.Participant}</p>

                      {/* Quick Info */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <div className={`w-8 h-8 rounded-lg bg-${genderStyle.color}-100 flex items-center justify-center`}>
                            <svg className={`w-4 h-4 text-${genderStyle.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <span className="font-medium text-gray-700">Gender:</span>
                          <span className="text-gray-900">{getGenderText(details.Gender)}</span>
                        </div>

                        <div className="flex items-center gap-2 text-sm">
                          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <span className="font-medium text-gray-700">Phone:</span>
                          <span className="text-gray-900">{details.Phone || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="md:w-2/3 space-y-4">
                    {/* Payment Status */}
                    <div className={`bg-gradient-to-br from-${details.PaymentStatus === 'Paid' ? 'green' : 'red'}-50 to-white rounded-xl p-5 border border-${details.PaymentStatus === 'Paid' ? 'green' : 'red'}-200`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Payment Amount</p>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(participant?.amount)}</p>
                        </div>
                        <div className={`px-4 py-2 rounded-lg bg-${details.PaymentStatus === 'Paid' ? 'green' : 'red'}-100`}>
                          <span className={`font-semibold text-${details.PaymentStatus === 'Paid' ? 'green' : 'red'}-700`}>
                            {details.PaymentStatus}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Registration Details */}
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                      <h4 className="font-semibold text-gray-700 mb-4">Registration Information</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Receipt No.</p>
                          <p className="font-mono font-medium text-gray-900">{details.Receipt_No || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Registration Date</p>
                          <p className="font-medium text-gray-900">{formatDate(details.Reg_date)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Institution Details */}
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                      <h4 className="font-semibold text-gray-700 mb-4">Institution Details</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500">Institution Name</p>
                          <p className="font-medium text-gray-900">{details.Inst_Name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Address</p>
                          <p className="text-sm text-gray-700">{details.Ins_Address || 'N/A'}</p>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">District</p>
                            <p className="font-medium text-gray-900">{details.District?.trim() || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">State</p>
                            <p className="font-medium text-gray-900">{details.State || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Pincode</p>
                            <p className="font-medium text-gray-900">{details.Pincode || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      <ImageZoomModal />
    </>
  );
};